import { Camera, Crown, Sparkles, X, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';

/**
 * BoscoCaptureUpsell — dormant-tier upsell modal.
 *
 * Spec: dormant users see BOSCO Capture™ visibly but get a premium upgrade
 * experience rather than the feature being hidden.
 */
export default function BoscoCaptureUpsell({ open, onClose, tier, isIndividual }) {
  const navigate = useNavigate();
  const upgradePath = '/preferences?tab=subscription';
  // v90 — BOSCO Capture is EXCLUSIVE to Elite regardless of user type.
  // School users no longer qualify; they see the same Elite upsell.
  const requiredTier = 'Elite';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="bosco-capture-upsell"
        className="max-w-md p-0 overflow-hidden border-0 bg-gradient-to-br from-emerald-50 to-white dark:from-emerald-950/40 dark:to-slate-950"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full p-1.5 hover:bg-slate-900/10 dark:hover:bg-white/10 transition-colors"
          data-testid="bosco-capture-upsell-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="px-6 pt-7 pb-3">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300 text-[10px] uppercase tracking-[0.25em] mb-3">
            <Sparkles className="h-3.5 w-3.5" />
            Exclusive to Elite · Beta
          </div>
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-shrink-0 shadow-lg shadow-emerald-600/30 relative">
              <Camera className="h-6 w-6" />
              <Crown className="absolute -top-1.5 -right-1.5 h-4 w-4 text-amber-300" />
            </div>
            <div>
              <h2 className="text-lg font-bold leading-snug">
                BOSCO Capture<sup className="text-xs">™</sup>
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                AI Educational Understanding Engine
              </p>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200/70 dark:border-slate-800">
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            Point your phone camera at any textbook, worksheet, or printed
            material — BOSCO will <strong>understand</strong> it and generate a
            complete teaching kit aligned to your board, class, and subject.
          </p>
          <ul className="mt-3 space-y-1.5 text-sm">
            {[
              'Mobile-first HD capture with live quality guidance',
              'Detects chapter, diagrams, tables, exercises, definitions',
              'Generates lesson plans, worksheets, assessments & more',
              'Privacy-first — captured media is auto-deleted after processing',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-slate-600 dark:text-slate-300">
                <span className="text-emerald-500 mt-1">●</span>
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="px-6 py-4 border-t border-slate-200/70 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40 flex items-center justify-between gap-3">
          <div className="text-[11px] text-slate-500 leading-relaxed">
            Your current plan: <strong className="capitalize">{tier || 'free'}</strong>.<br />
            Available exclusively with the <strong>{requiredTier}</strong> subscription.
          </div>
          <Button
            onClick={() => {
              onClose?.();
              const targetPlan = isIndividual ? 'elite' : 'pro';
              // Hard navigation for the same reason as BoscoCapturePage.
              window.location.assign(`/teacher-assignments?upgrade=${targetPlan}&reason=bosco-capture`);
            }}
            data-testid="bosco-capture-upsell-cta"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Upgrade <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
